<div class="mx-auto flex max-w-[1180px] flex-col gap-8 p-6 pb-24">

  <!-- EB-3709: Read-only banner — surfaces only when the user lacks
       amie.brandManager.write. The form group is also fully .disable()'d in
       this mode so the inputs cannot accept changes. -->
  <cmt-brand-readonly-banner />

  <!-- Hero card — actions live at the bottom sticky bar, so the hero no longer
       needs to be sticky itself. -->
  <section class="rounded-2xl border border-border bg-card p-6 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="flex min-w-0 flex-1 items-center gap-7">
        @if (editing()) {
          <cmt-brand-logo
            class="shrink-0"
            [domain]="brand()?.domain"
            variant="hero"
            heightOverride="3rem"
          />
        }
        <div class="flex min-w-0 flex-col gap-2">
          <p class="font-mono-feat text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            <a routerLink="/admin/brands" class="hover:text-primary">Admin</a>
            <span class="mx-2 opacity-50">/</span>
            <a routerLink="/admin/brands" class="hover:text-primary">Brands</a>
            <span class="mx-2 opacity-50">/</span>
            @if (editing()) { {{ brand()?.name }} } @else { New Brand }
          </p>
          <h1 class="font-display text-3xl font-semibold leading-tight text-foreground">
            @if (editing()) { {{ brand()?.name }} } @else { New Brand }
          </h1>
          @if (editing()) {
            <div class="flex items-center gap-2.5 text-xs">
              <span class="font-mono-feat text-muted-foreground">ID {{ brand()?.id }}</span>
              <span class="h-3 w-px bg-border"></span>
              @if (brand()?.active) {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2 py-0.5 font-medium text-success">
                  <span class="h-1.5 w-1.5 rounded-full bg-current"></span> Active
                </span>
              } @else {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-destructive/15 px-2 py-0.5 font-medium text-destructive">
                  <span class="h-1.5 w-1.5 rounded-full bg-current"></span> Inactive
                </span>
              }
            </div>
          }
        </div>
      </div>

    </div>
  </section>

  <!-- Form — card per section for clearer visual grouping across the 15 fields.
       Each section gets its own left-accent colour so they're visually distinct
       at a glance while scrolling. -->
  <form [formGroup]="form" class="flex flex-col gap-7" (ngSubmit)="onSubmit()">

    <!-- Identity -->
    <section class="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header class="flex items-baseline gap-3 border-b border-border pb-3">
        <span class="h-5 w-1 rounded-full bg-primary"></span>
        <h2 class="font-display text-lg font-semibold text-foreground">Identity</h2>
      </header>

      <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
        <hlm-form-field>
          <label hlmLabel for="brandName" class="text-xs">Brand Name *</label>
          <input hlmInput id="brandName" type="text" formControlName="brandName" placeholder="Name shown across the platform" />
          @if (form.controls.brandName.touched && form.controls.brandName.invalid) {
            <p hlmFieldError class="text-xs">Required.</p>
          }
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="defaultDomain" class="text-xs">Default Domain *</label>
          <input hlmInput id="defaultDomain" type="text" formControlName="defaultDomain" placeholder="example.com" class="font-mono-feat" />
          @if (form.controls.defaultDomain.touched && form.controls.defaultDomain.invalid) {
            <p hlmFieldError class="text-xs">
              @if (form.controls.defaultDomain.errors?.['required']) { Required. }
              @else { Must look like a domain — e.g. brand.com. }
            </p>
          }
        </hlm-form-field>

        <hlm-form-field class="md:col-span-2">
          <label hlmLabel for="description" class="text-xs">Description</label>
          <textarea hlmTextarea id="description" formControlName="description" rows="2" placeholder="Optional internal note"></textarea>
        </hlm-form-field>
      </div>
    </section>

    <!-- Localization -->
    <section class="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header class="flex items-baseline gap-3 border-b border-border pb-3">
        <span class="h-5 w-1 rounded-full bg-[oklch(0.62_0.15_190)]"></span>
        <h2 class="font-display text-lg font-semibold text-foreground">Localization</h2>
      </header>

      <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
        <hlm-form-field>
          <label hlmLabel for="f-country" class="text-xs">Country *</label>
          <hlm-select id="f-country" [options]="countryOptions()" [loading]="dialingCodesLoading()" [searchable]="true" placeholder="Select country…" loadingPlaceholder="Loading countries…" searchPlaceholder="Search country…" formControlName="countryId" />
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="f-city" class="text-xs">Default City</label>
          <hlm-select id="f-city" [options]="cityOptions()" [loading]="dialingCodesLoading()" [searchable]="true" placeholder="Select city…" loadingPlaceholder="Loading cities…" searchPlaceholder="Search city…" formControlName="defaultCityId" />
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="f-language" class="text-xs">Default Language *</label>
          <hlm-select id="f-language" [options]="languageOptions()" [searchable]="true" placeholder="Select language…" searchPlaceholder="Search language…" formControlName="defaultLanguage" />
        </hlm-form-field>
      </div>
    </section>

    <!-- Telephony -->
    <section class="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header class="flex items-baseline gap-3 border-b border-border pb-3">
        <span class="h-5 w-1 rounded-full bg-[oklch(0.70_0.14_65)]"></span>
        <h2 class="font-display text-lg font-semibold text-foreground">Telephony</h2>
      </header>

      <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
        <hlm-form-field>
          <label hlmLabel for="f-skillset" class="text-xs">Default Skillset *</label>
          <hlm-select id="f-skillset" [options]="skillsetOptions()" placeholder="Select skillset…" formControlName="defaultSkillsetId" />
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="f-skill" class="text-xs">Default Skill *</label>
          <hlm-select id="f-skill" [options]="skillOptions()" [searchable]="true" placeholder="Select skill…" searchPlaceholder="Search skill…" formControlName="defaultSkill" />
        </hlm-form-field>
      </div>
    </section>

    <!-- Pricing & Billing -->
    <section class="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header class="flex items-baseline gap-3 border-b border-border pb-3">
        <span class="h-5 w-1 rounded-full bg-[oklch(0.58_0.18_295)]"></span>
        <h2 class="font-display text-lg font-semibold text-foreground">Pricing &amp; Billing</h2>
      </header>

      <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
        <hlm-form-field>
          <label hlmLabel for="f-pricelist" class="text-xs">Price List *</label>
          <cmt-price-list-picker id="f-pricelist" [priceLists]="priceLists()" formControlName="defaultPriceList" />
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="f-vat" class="text-xs">VAT Regime *</label>
          <hlm-select id="f-vat" [options]="vatRegimeOptions()" [searchable]="true" placeholder="Select VAT regime…" searchPlaceholder="Search regime…" formControlName="defaultVATCountry" />
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="f-bookkeeping" class="text-xs">Bookkeeping Configuration</label>
          <hlm-select id="f-bookkeeping" [options]="bookkeepingOptions()" [searchable]="true" placeholder="Select configuration…" searchPlaceholder="Search configuration…" formControlName="bookkeepingConfigurationId" />
        </hlm-form-field>

        <hlm-form-field>
          <label hlmLabel for="f-fee-limit" class="text-xs">
            Service Fee Limit
            @if (editing() && brand()?.currency) {
              <span class="ml-1 font-mono-feat text-muted-foreground">({{ brand()?.currency }})</span>
            }
            *
          </label>
          <input id="f-fee-limit" hlmInput cmtNoNumberStep type="number" min="0" step="0.01" formControlName="defaultServiceFeeLimit" class="font-mono-feat" />
          @if (!editing()) {
            <p hlmFieldHint class="text-xs">In the brand's currency — assigned by the backend based on country.</p>
          }
        </hlm-form-field>

        <div class="flex items-center gap-3 pt-7">
          <hlm-switch formControlName="basicFeeInAdvance" />
          <div class="flex flex-col">
            <span class="text-sm font-medium leading-tight">Basic Fee In Advance</span>
            <span class="text-xs text-muted-foreground">Invoice basic fees up-front instead of in arrears.</span>
          </div>
        </div>

        <div class="flex items-center gap-3 pt-7">
          <hlm-switch formControlName="defaultHolidayAsSunday" />
          <div class="flex flex-col">
            <span class="text-sm font-medium leading-tight">Default: Holidays As Sunday</span>
            <span class="text-xs text-muted-foreground">Public holidays count as a Sunday for customer-side routing. Agents are unaffected. Overridable per phone service.</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Activation (edit mode only) -->
    @if (editing()) {
      <section class="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <header class="flex items-baseline gap-3 border-b border-border pb-3">
          <span class="h-5 w-1 rounded-full bg-destructive"></span>
          <h2 class="font-display text-lg font-semibold text-foreground">Activation</h2>
        </header>

        <div class="flex flex-wrap items-start gap-4 rounded-lg border border-border bg-muted/40 p-4">
          <div class="flex-1 min-w-[260px]">
            <div class="mb-2 flex items-center gap-2">
              @if (brand()?.active) {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-medium text-success">
                  <span class="h-1.5 w-1.5 rounded-full bg-current"></span> Active
                </span>
              } @else {
                <span class="inline-flex items-center gap-1.5 rounded-full bg-destructive/15 px-2.5 py-0.5 text-xs font-medium text-destructive">
                  <span class="h-1.5 w-1.5 rounded-full bg-current"></span> Inactive
                </span>
              }
            </div>
            <p class="text-sm text-foreground">
              @if (brand()?.active) {
                This brand is live. New customers can be onboarded and billing runs on schedule.
              } @else {
                This brand is turned off. It stays in the system but won't accept new customers and is hidden from most lists.
              }
            </p>
          </div>

          <!-- EB-3709 M2: native title sits directly on the button. The previous
               wrapper-span pattern was unreliable across browsers for disabled
               buttons — matches the consistent on-button pattern used by
               brand-list / number-blocks. -->
          @if (brand()?.active) {
            <button hlmBtn variant="outline" type="button" (click)="onDeactivate()"
              [disabled]="togglingActivation() || !canWrite()"
              [attr.title]="!canWrite() ? 'Read-only access — needs amie.brandManager.write to enable' : null"
              class="border-destructive text-destructive hover:bg-destructive/10">
              @if (togglingActivation()) { <hlm-spinner size="16" /> }
              Deactivate Brand
            </button>
          } @else {
            <button hlmBtn variant="outline" type="button" (click)="onActivate()"
              [disabled]="togglingActivation() || !canWrite()"
              [attr.title]="!canWrite() ? 'Read-only access — needs amie.brandManager.write to enable' : null"
              class="border-success text-success hover:bg-success/10">
              @if (togglingActivation()) { <hlm-spinner size="16" /> }
              Activate Brand
            </button>
          }
        </div>
      </section>
    }
  </form>

  <!-- Domains — embedded management panel (edit mode only). Replaces the old
       "Domains" nav button; the content lives inline here now. The standalone
       /admin/brands/:id/domains route renders the same panel. -->
  @if (editing() && brand(); as b) {
    <cmt-brand-domains-panel [brandId]="b.id" [brandName]="b.name" />

    <!-- Number Blocks — embedded management panel (edit mode only). Replaces
         the old "Number Blocks" hero nav button; the content lives inline here
         now. The standalone /admin/brands/:id/numberblocks route renders the
         same panel. -->
    <cmt-brand-number-blocks-panel [brandId]="b.id" [brandName]="b.name" />
  }

  <!-- Fixed action footer — viewport-pinned (not sticky) so the bar never
       releases at the scroll-end and "jumps up". `bottom-8` clears the 32px
       SpikeFooter; the form container's pb-24 leaves matching room behind it.
       Inner div re-applies the same max-width as the form so the bar lines up
       visually with the cards above. -->
  <section class="fixed inset-x-0 bottom-8 z-30 border-t border-border/60 bg-card/95 shadow-[0_-10px_24px_-12px_rgba(20,24,40,0.18),0_-2px_4px_-2px_rgba(20,24,40,0.06)] backdrop-blur supports-[backdrop-filter]:bg-card/80">
    <div class="mx-auto flex max-w-[1180px] items-center justify-between gap-3 px-6 py-3">
    <div class="text-xs">
      @if (saving()) {
        <span class="text-muted-foreground">Saving…</span>
      } @else if (justSaved()) {
        <span class="font-medium text-success">Saved.</span>
      } @else if (editing() && dirty()) {
        <span class="inline-flex items-center gap-1.5 font-medium text-warning">
          <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
          Unsaved changes
        </span>
      } @else if (saveDisabledReason()) {
        <span class="text-muted-foreground">{{ saveDisabledReason() }}</span>
      }
    </div>
    <div class="flex items-center gap-2">
      <button hlmBtn variant="outline" type="button" (click)="onCancel()" [disabled]="saving()">
        @if (editing() && !dirty()) { Close } @else { Cancel }
      </button>
      <!-- Span wrapper catches hover even when the inner button is disabled
           (browsers suppress events on disabled controls). -->
      <span [attr.title]="saveDisabledReason() || null">
        <button hlmBtn variant="default" type="button" (click)="onSubmit()"
          class="disabled:opacity-70"
          [disabled]="formInvalid() || saving() || (editing() && !dirty()) || !canWrite()"
          [attr.aria-label]="saving() ? 'Saving in progress' : (editing() ? 'Save brand changes' : 'Create brand')"
          [attr.aria-busy]="saving() ? 'true' : null">
          @if (saving()) {
            <hlm-spinner size="16" /> Saving…
          } @else if (justSaved()) {
            <hlm-icon name="lucideCheck" size="0.9rem" /> Saved
          } @else {
            <hlm-icon name="lucideSave" size="0.9rem" />
            @if (editing()) { Save Changes } @else { Create Brand }
          }
        </button>
      </span>
    </div>
    </div>
  </section>
</div>
